import {
  useMutation,
  useQueryClient,
  useQuery,
  MutationOptions,
  UseQueryOptions,
  UseMutationOptions,
} from 'react-query'
import {
  AddMembershipChargePayment,
  CreateMembershipSmsRequest,
  Customer,
  Membership,
  MembershipChargeResponse,
  MembershipsApi as MembershipsApiTs,
  ResponseError,
  ShowMembership,
  ShowMembershipSms,
} from '@trybeapp/sdk'
import { MembershipsApi } from '@trybeapp/sdk.customer'
import { BasketApi } from '@trybeapp/sdk.shop'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { DateTime } from 'luxon'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { toIsoDate } from 'utilities/DateUtils/dateUtils'
import { ApiResponse, LegacySdkResponseError, PaginatedApiResponse } from 'api/api.types'

const legacyApi = new MembershipsApi()
const basketApi = new BasketApi()
const api = new MembershipsApiTs(apiConfiguration)

export const useMembershipsForCustomer = (
  customerId: Customer['id'],
  options: UseQueryOptions<ApiResponse<Membership[]>, LegacySdkResponseError> = {}
) =>
  useQuery<ApiResponse<Membership[]>, LegacySdkResponseError>(
    ['memberships', customerId],
    () => legacyApi.listMemberships({ customerId }),
    options
  )

export const useMemberships = (
  params: Record<string, any> = {},
  options: UseQueryOptions<PaginatedApiResponse<Membership>, LegacySdkResponseError> = {}
) => {
  params.siteId = useCurrentSiteId()
  params.createdAtFrom = params.createdAtFrom ? toIsoDate(params.createdAtFrom) : null
  params.createdAtTo = params.createdAtTo ? toIsoDate(params.createdAtTo) : null
  params.endDateFrom = params.endDateFrom ? toIsoDate(params.endDateFrom) : null
  params.endDateTo = params.endDateTo ? toIsoDate(params.endDateTo) : null
  params.nextBillingDateFrom = params.nextBillingDateFrom
    ? DateTime.fromJSDate(params.nextBillingDateFrom).toISODate()
    : null
  params.nextBillingDateTo = params.nextBillingDateTo
    ? DateTime.fromJSDate(params.nextBillingDateTo).toISODate()
    : null

  return useQuery<PaginatedApiResponse<Membership>, LegacySdkResponseError>(
    ['memberships', { ...params }],
    () => legacyApi.listMemberships(params),
    {
      cacheTime: Infinity,
      staleTime: 60 * 60 * 1000, // 60 minutes
      ...options,
    }
  )
}

export const useExportMemberships = () => {
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse<any>, LegacySdkResponseError, Record<string, any>>(
    ['membershipExport'],
    (params = {}) => {
      params.siteId = siteId
      params.createdAtFrom = params.createdAtFrom ? toIsoDate(params.createdAtFrom) : null
      params.createdAtTo = params.createdAtTo ? toIsoDate(params.createdAtTo) : null
      params.endDateFrom = params.endDateFrom ? toIsoDate(params.endDateFrom) : null
      params.endDateTo = params.endDateTo ? toIsoDate(params.endDateTo) : null
      params.nextBillingDateFrom = params.nextBillingDateFrom
        ? toIsoDate(params.nextBillingDateFrom)
        : null
      params.nextBillingDateTo = params.nextBillingDateTo
        ? toIsoDate(params.nextBillingDateTo)
        : null

      return legacyApi.queueListMemberships(params)
    }
  )
}

export const useCustomerMembership = (
  membershipId: Membership['id'],
  options: UseQueryOptions<ApiResponse<Membership>, LegacySdkResponseError> = {}
) =>
  useQuery<ApiResponse<Membership>, LegacySdkResponseError>(
    ['membership', membershipId],
    () => legacyApi.getMembership(membershipId),
    options
  )

export const useMembership = (
  membershipId: Membership['id'],
  options: UseQueryOptions<ShowMembership, ResponseError> = {}
) =>
  useQuery<ShowMembership, ResponseError>(
    ['membershipTs', membershipId],
    () => api.getMembership({ membershipId }),
    options
  )

export const useConfirmMembership = () => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse, LegacySdkResponseError, Membership['id']>(
    (membershipId) => legacyApi.confirmMembership(membershipId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useCreateCustomerMembership = (
  customerId: string,
  options: UseMutationOptions<ApiResponse<any>, LegacySdkResponseError, Record<string, any>> = {}
) => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse<any>, LegacySdkResponseError, Record<string, any>>(
    async (membership) =>
      basketApi.createMembershipOrder({
        customer_id: customerId,
        site_id: siteId,
        ...membership,
      }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries(['memberships'])
        if (options.onSuccess) {
          options.onSuccess(...args)
        }
      },
    }
  )
}

export const useCancelMembership = (membershipId?: string) => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse, LegacySdkResponseError, string | null>(
    (passedMembershipId = null) => legacyApi.cancelMembership(membershipId ?? passedMembershipId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useRequestMandate = (membershipId: Membership['id']) =>
  useMutation<ApiResponse, LegacySdkResponseError, void>(() =>
    legacyApi.requestMandate(membershipId)
  )

export const useRequestMandateForMembership = () =>
  useMutation<ApiResponse, LegacySdkResponseError, Membership['id']>((membershipId) =>
    legacyApi.requestMandate(membershipId)
  )

export const useRetryPaymentForMembership = () =>
  useMutation<ApiResponse, LegacySdkResponseError, Membership['id']>((membershipId) =>
    legacyApi.retryPayment(membershipId)
  )

export const useUpdateMembership = (
  membershipId: Membership['id'],
  options: UseMutationOptions<
    ApiResponse<Membership>,
    LegacySdkResponseError,
    Record<string, any>
  > = {}
) => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Membership>, LegacySdkResponseError, Record<string, any>>(
    (updateMembershipRequest) =>
      legacyApi.updateMembership(membershipId, { updateMembershipRequest }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries(['memberships'])
        options.onSuccess?.(...args)
      },
    }
  )
}

export const useAddMembershipPayment = (
  options: MutationOptions<
    ApiResponse,
    LegacySdkResponseError,
    { membershipId: Membership['id']; paymentMethodId: string }
  > = {}
) => {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse,
    LegacySdkResponseError,
    { membershipId: Membership['id']; paymentMethodId: string }
  >(
    ({ membershipId, paymentMethodId }) =>
      legacyApi.addManualPayment(membershipId, {
        addManualPaymentRequest: {
          processor_type_id: paymentMethodId,
        },
      }),
    {
      ...options,
      onSuccess: (...args) => {
        // Invalidate the list of memberships because we've updated the next billing date of this one.
        queryClient.invalidateQueries(['memberships'])
        options.onSuccess?.(...args)
      },
    }
  )
}

export const useAddMembershipChargePayment = () => {
  const queryClient = useQueryClient()

  return useMutation<MembershipChargeResponse, ResponseError, [string, AddMembershipChargePayment]>(
    ([chargeId, addMembershipChargePayment]) => {
      return api.actionAddMembershipChargePayment({
        chargeId,
        addMembershipChargePayment,
      })
    },
    {
      onSuccess: (response: MembershipChargeResponse) => {
        queryClient.invalidateQueries([
          'chargesForMembership',
          { membershipId: response.data.membership.id },
        ])
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useProcessMembershipChargePayment = () => {
  const queryClient = useQueryClient()

  return useMutation<MembershipChargeResponse, ResponseError, [string]>(
    ([chargeId]) => {
      return api.actionProcessMembershipCharge({
        chargeId,
      })
    },
    {
      onSuccess: (response: MembershipChargeResponse) => {
        queryClient.invalidateQueries([
          'chargesForMembership',
          { membershipId: response.data.membership.id },
        ])
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useRetryMembershipChargePayment = () => {
  const queryClient = useQueryClient()

  return useMutation<MembershipChargeResponse, ResponseError, [string]>(
    ([chargeId]) => {
      return api.actionRetryMembershipCharge({
        chargeId,
      })
    },
    {
      onSuccess: (response: MembershipChargeResponse) => {
        queryClient.invalidateQueries([
          'chargesForMembership',
          { membershipId: response.data.membership.id },
        ])
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useArchiveMembership = () => {
  const queryClient = useQueryClient()

  return useMutation<void, LegacySdkResponseError, Membership['id']>(
    (membershipId) => legacyApi.archiveMembership(membershipId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['memberships'])
      },
    }
  )
}

export const useNextBillingDetails = (
  membershipId: Membership['id'],
  options: UseQueryOptions<ApiResponse<Record<string, any>>, LegacySdkResponseError> = {}
) =>
  useQuery<ApiResponse<Record<string, any>>, LegacySdkResponseError>(
    ['membershipNextBillingDetails', membershipId],
    () => legacyApi.nextBillingDetails(membershipId),
    options
  )

export const useSubsequentBillingDetails = (
  membershipId: Membership['id'],
  options: UseQueryOptions<ApiResponse<Record<string, any>>, LegacySdkResponseError> = {}
) =>
  useQuery<ApiResponse<Record<string, any>>, LegacySdkResponseError>(
    ['membershipSubsequentBillingDetails', membershipId],
    () => legacyApi.subsequentBillingDetails(membershipId),
    options
  )

export const useSendMembershipSms = () =>
  useMutation<ShowMembershipSms, ResponseError, CreateMembershipSmsRequest>((params) =>
    api.createMembershipSms(params)
  )
